import moment from "moment";
import React, { useContext } from "react";

import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  DateIcon,
  LocationIcon,
  MapIcon,
} from "../../constant/icon";
import { AuthContext } from "../../contexts/AuthContext";
import { useLanguage } from "../../hooks/useLanguage";
import { getMatchStatusAction } from "../../utils/getMatchStatusAction";
import BaseImage from "../baseImage/BaseImage";
import BaseButton from "../button/BaseButton";
import { useRouter } from "next/router";
import { FOOD_COURT, SPORT } from "../../constant/constants";

export default function MatchBookNowCard({
  onBookNow,
  currentStep,
  bgImageUrl,
  itemDetail,
  isHasTicketType,
  ticketTypes,
  oldOrder,
}) {
  const { teamGuest, teamHost } = itemDetail?.metaData;
  const startTime = itemDetail?.startTime;
  const { t } = useTranslation();

  const { user } = useContext(AuthContext);
  const { nameWithLanguage, currentLanguage } = useLanguage();
  const router = useRouter();
  const isSport = itemDetail.category === SPORT;
  const isFoodCourt = itemDetail.category === FOOD_COURT;

  const buttonBookNowStatus = getMatchStatusAction({
    matchInfo: itemDetail,
    ticketTypes,
    t,
    user,
    oldOrder,
    isHasTicketType,
  });

  return (
    <div className="w-full">
      <div className="flex flex-col bg-white xl:flex-col lg:flex-col md:flex-row md:shadow-base md:rounded-r-lg lg:rounded-lg">
        <div className="relative w-full h-200 md:h-auto md:w-1/2 lg:h-56 lg:w-full">
          <BaseImage
            className="md:rounded-l-lg lg:rounded-none md:h-full md:w-full lg:w-full "
            backgroundSize="contain"
            src={bgImageUrl}
          />

          <div className="absolute top-0 w-full h-full lg:rounded-t-lg md:rounded-l-lg opacity-80" />
        </div>
        <div className="p-6 md:w-1/2 md:rounded-r-lg lg:w-full">
          {isSport ? (
            <p
              color="secondary-400"
              className="pb-4 font-bold text-purple-900 lg:mt-4 sm:text-title-1 text-title-2"
            >
              {teamHost ? teamHost[nameWithLanguage] : ""} Vs{" "}
              {teamGuest ? teamGuest[nameWithLanguage] : ""}
            </p>
          ) : (
            <p
              color="secondary-400"
              className="pb-4 font-bold text-purple-900 lg:mt-4 sm:text-title-1 text-title-2"
            >
              {itemDetail ? itemDetail[nameWithLanguage] : ""}
            </p>
          )}
          {isSport && (
            <p
              className={
                "text-neutral-600 font-bold text-body-2 md:text-body-1 lg:text-body-1"
              }
            >
              {itemDetail ? itemDetail[nameWithLanguage] : ""}
            </p>
          )}

          <div className="flex flex-col mt-6 divide-y gap-y-4 divide-solid md:divide-none ">
            <MatchDateInfo
              Icon={() => <DateIcon />}
              title={t("date")}
              value={moment(startTime).format("MMMM Do YYYY")}
            />
            <MatchDateInfo
              Icon={() => <ClockIcon />}
              title={t("open_hour")}
              value={moment(startTime).format("LT")}
            />
            <MatchDateInfo
              Icon={() => <MapIcon />}
              title={t("city")}
              value={
                currentLanguage === "ar"
                  ? itemDetail?.cityAr
                  : itemDetail?.cityEn
              }
            />
            <MatchDateInfo
              Icon={() => <LocationIcon />}
              title={t("location")}
              value={
                currentLanguage === "ar"
                  ? itemDetail?.locationAr
                  : itemDetail?.locationEn
              }
            />
          </div>
          {ticketTypes.length > 0 && itemDetail?.status !== "ENDED" && (
            <div className="flex flex-col justify-center gap-4 mt-6">
              <p className="font-bold text-purple-900 text-body-1 sm:text-title-2">
                {t("ticket_types")}
              </p>
              <div className="flex flex-col gap-3">
                {ticketTypes.map((type) => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between pb-3 border-b border-solid border-neutral-200"
                  >
                    <p className="text-neutral-600 text-body-2">
                      {type ? type[nameWithLanguage] : ""}
                    </p>
                    <p className="font-bold text-purple-900 text-body-2 ">
                      {currentLanguage === "en"
                        ? `${type?.price} SR`
                        : `SR ${type?.price}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentStep === 0 && (
            <div className="w-full pt-10">
              <BaseButton
                disable={buttonBookNowStatus.isDisable}
                onClick={() => {
                  if (buttonBookNowStatus?.url) {
                    return router.push(buttonBookNowStatus?.url);
                  }
                  onBookNow();
                }}
                sizeButton="lg"
                variant="filled"
                className="w-full"
              >
                <p className="font-bold text-body-1">
                  {buttonBookNowStatus.title}
                </p>
              </BaseButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const MatchDateInfo = ({
  title = "",
  value = "",
  Icon = () => <DateIcon />,
}) => {
  return (
    <div className="flex flex-row items-center pt-4 md:pt-0 gap-x-2">
      <Icon />
      <div className="flex flex-row items-center justify-between w-full md:justify-start gap-x-2">
        <p className={"font-bold text-body-2 md:text-body-1 text-purple-900"}>
          {title}
        </p>
        <p
          size="md"
          className="font-normal capitalize text-body-2 text-neutral-500 md:text-body-1"
        >
          {value}
        </p>
      </div>
    </div>
  );
};
